import React, { lazy, Suspense, useEffect } from 'react';
import { ChangeEvent, FocusEvent, KeyboardEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { useSharedState } from '@philips-internal/oswald-ui-engine';
import { classNames, isFunction, sanitizeHTML, trigger } from '@philips-internal/oswald-ui-utils';
import { Loader } from '../loader/loader.js';
import { TEXT_EDITOR_FONT_SIZES, TEXT_EDITOR_FORMATS, TEXT_EDITOR_THEME, TEXT_EDITOR_TOOLBAR_OPTIONS } from './text-editor-constants.js';
import { TextEditorProperties } from './text-editor-types.js';
import './text-editor.css';
const ReactQuill = lazy(async () => {
    const quill = await import('react-quill');
    /**
     * This is needed because we will use font sizes as pixel values and not fixed names
     */
    const size = quill.Quill.import('attributors/style/size');
    size.whitelist = TEXT_EDITOR_FONT_SIZES;
    quill.Quill.register(size, true);
    return quill;
});
function TextEditor(props) {
    const { className, enabled = TextEditorProperties.enabled.defaultValue, onBlur, onChange, onFocus, onKeyDown, onKeyPress, onKeyUp, readOnly = TextEditorProperties.readOnly.defaultValue, textOnly = TextEditorProperties.textOnly.defaultValue, value = TextEditorProperties.value.defaultValue, } = props;
    const [internalValue, setInternalValue] = useSharedState({
        initialValue: '',
        name: 'internalValue',
        props,
    });
    useEffect(() => {
        setInternalValue(value);
    }, [value]);
    const onTextEditorChange = (value) => {
        setInternalValue((prevInternalValue) => {
            if (!isFunction(onChange)) {
                return value;
            }
            onChange(new ChangeEvent({
                oldValue: prevInternalValue,
                value,
            }));
            return value;
        });
    };
    const textEditorClassName = classNames({
        'c-text-editor': true,
        'c-text-editor--disabled': !enabled,
        'c-text-editor--text-only': Boolean(textOnly),
        'u-scrollbar': true,
        [`${className}`]: Boolean(className),
    });
    return (React.createElement(Suspense, { fallback: React.createElement(Loader, null) },
        React.createElement(ReactQuill, { className: textEditorClassName, formats: TEXT_EDITOR_FORMATS, modules: { toolbar: TEXT_EDITOR_TOOLBAR_OPTIONS }, onBlur: trigger(onBlur, new FocusEvent()), onChange: (value) => onTextEditorChange(sanitizeHTML(value)), onFocus: trigger(onFocus, new FocusEvent()), onKeyDown: (event) => trigger(onKeyDown, new KeyboardEvent(event)), onKeyPress: (event) => trigger(onKeyPress, new KeyboardEvent(event)), onKeyUp: (event) => trigger(onKeyUp, new KeyboardEvent(event)), readOnly: readOnly || !enabled || textOnly, theme: TEXT_EDITOR_THEME, value: internalValue })));
}
export { TextEditor, };
//# sourceMappingURL=text-editor.js.map